import { normalizedText } from '@domain/product'
import type { Product } from '@domain/product'

/**
 * O texto que vai para a busca externa: nome, marca, tamanho e fardo — nessa
 * ordem, e só o que existe.
 *
 * ## A marca só entra quando o nome não a tem
 *
 * O caso que motivou a regra é real e está no catálogo: `name` é *"Brutal fruit
 * ruby Apple"* e `brand` é *"Brutal"*. Concatenar os dois manda "Brutal Brutal
 * fruit ruby Apple" para o buscador — uma repetição que **piora** o resultado,
 * porque o motor passa a pesar duas vezes um termo que não distingue nada.
 *
 * A comparação é por `normalizedText`, e não por `includes` cru: "MELITTA" em
 * `brand` e "Café Melitta" em `name` são a mesma marca, e um `includes` sobre o
 * texto original responderia que não.
 *
 * O **tamanho** cai na mesma armadilha e recebe a mesma guarda: "Café Melitta
 * 500g" com `size: 500, unit: 'g'` sairia como *"Café Melitta 500g 500 g"*.
 * Funciona porque `normalizedText` separa dígito de unidade — "500g" e "500 g"
 * normalizam para o mesmo texto, e por isso a comparação enxerga a repetição
 * que um `includes` cru deixaria passar.
 *
 * ## Tamanho pela metade não entra
 *
 * `size` sem `unit` (ou o contrário) é o estado de um produto recém-criado —
 * comum, não excepcional. Mandar "275" solto para a busca não ajuda, e mandar
 * "275 null" é o tipo de erro que só aparece depois de publicado.
 */
export function termoDeBusca(p: Product): string {
  const partes: string[] = []

  const nome = normalizedText(p.name)

  const marca = p.brand?.trim() ?? ''
  if (marca !== '' && !nome.includes(normalizedText(marca))) partes.push(marca)
  partes.push(p.name.trim())

  if (p.size !== null && p.unit !== null) {
    const tamanho = `${String(p.size)} ${p.unit}`
    if (!nome.includes(normalizedText(tamanho))) partes.push(tamanho)
  }
  if (p.packQuantity > 1) partes.push(`${String(p.packQuantity)} unidades`)

  return partes.join(' ')
}

/** A busca do Google já com o termo montado e escapado. */
export function urlDoGoogle(p: Product): string {
  return `https://www.google.com/search?q=${encodeURIComponent(termoDeBusca(p)).replace(/%20/g, '+')}`
}

/**
 * O geohash da casa, em Curitiba — é o `local` que o Nota Paraná usa para
 * ordenar por distância.
 *
 * **Constante, e não campo de ajuste.** São duas pessoas numa casa só; uma tela
 * de configuração de localização seria construir para um usuário que não existe
 * (princípio 8). Mudou de casa, muda esta linha.
 *
 * Nove caracteres de geohash são ~5 metros — isto é o endereço, não o bairro.
 * Vive no código porque o repositório é privado; num repositório público seria
 * segredo, não constante.
 */
const GEOHASH_DA_CASA = '6gkzm2dkm'

/**
 * A busca do Nota Paraná, que compara o preço do MESMO código de barras nas
 * notas fiscais emitidas perto daqui.
 *
 * Usa `barcode` — o código como está impresso —, e **não** `gtin`: aquele é
 * normalizado para GTIN-14 com zeros à esquerda (`gtinOf` faz
 * `padStart(14, '0')`), e um `00070847022503` não é o que o site espera.
 */
export function urlDoNotaParana(barcode: string): string {
  const termo = encodeURIComponent(barcode.trim())
  return `https://menorpreco.notaparana.pr.gov.br/app?termo=${termo}&local=${GEOHASH_DA_CASA}`
}
